/**
 * OAuth 2.0 Authorization Code with PKCE (RFC 7636).
 *
 * clip2commons is a public client running entirely in the browser. There is
 * no confidential client secret because we cannot keep one secret. PKCE
 * binds the authorization code to a per-flow code_verifier that the
 * legitimate caller knows; an attacker who steals the redirect-back code
 * cannot redeem it without the verifier.
 *
 * Wikimedia OAuth 2.0 supports PKCE; the consumer must be registered as a
 * non-confidential / public client (no secret).
 */

import {
  OAUTH_CLIENT_ID, OAUTH_CLIENT_SECRET, OAUTH_REDIRECT_URI,
  OAUTH_AUTHORIZE_URL, OAUTH_TOKEN_URL,
  HAS_USER_OAUTH, LEGACY_ACCESS_TOKEN
} from "./config"

const STORAGE_KEY_TOKEN = "c2c_oauth_token"
const STORAGE_KEY_REFRESH = "c2c_oauth_refresh"
const STORAGE_KEY_EXPIRY = "c2c_oauth_expiry"
const STORAGE_KEY_STATE = "c2c_oauth_state"
const STORAGE_KEY_VERIFIER = "c2c_oauth_verifier"
const STORAGE_KEY_USERNAME = "c2c_oauth_username"
/** Opt-in "keep me signed in" flag. In localStorage because the choice has
 *  to survive the redirect to meta and back. */
const STORAGE_KEY_REMEMBER = "c2c_oauth_remember"

/**
 * How long a remembered session may be resumed for.
 *
 * Access tokens live ~4 hours and stay in sessionStorage always. Only the
 * refresh token is mirrored to localStorage, and only when the user asks
 * for it: that is what lets a new tab weeks later sign in with no clicks.
 * It is stamped when written and ignored past this age, so an abandoned
 * browser stops being a way into someone's account. Wikimedia may expire
 * the token sooner, which surfaces as a refused refresh and clears it.
 */
const PERSIST_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

interface TokenResponse {
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
}

export function isUserOAuthMode(): boolean { return HAS_USER_OAUTH }

export function getAccessToken(): string | null {
  if (!HAS_USER_OAUTH) return LEGACY_ACCESS_TOKEN || null
  const token = sessionStorage.getItem(STORAGE_KEY_TOKEN)
  const expiryStr = sessionStorage.getItem(STORAGE_KEY_EXPIRY)
  if (!token || !expiryStr) return null
  if (Date.now() >= parseInt(expiryStr, 10)) return null
  return token
}

/** Whether the user asked to stay signed in on this device. */
function rememberWanted(): boolean {
  try { return localStorage.getItem(STORAGE_KEY_REMEMBER) === "1" } catch { return false }
}

/** Record (or revoke) the "keep me signed in" choice. Turning it off also
 *  drops the mirrored refresh token immediately. */
export function setRememberMe(on: boolean): void {
  try {
    if (on) {
      localStorage.setItem(STORAGE_KEY_REMEMBER, "1")
    } else {
      localStorage.removeItem(STORAGE_KEY_REMEMBER)
      localStorage.removeItem(STORAGE_KEY_REFRESH)
    }
  } catch { /* private mode - the session simply won't outlive the tab */ }
}

/** The mirrored refresh token, if one is stored and still inside the
 *  PERSIST_MAX_AGE_MS ceiling. A stale entry is removed on sight. */
function persistedRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_REFRESH)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { v?: string; t?: number }
    if (!parsed.v || !parsed.t) return null
    if (Date.now() - parsed.t > PERSIST_MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY_REFRESH)
      return null
    }
    return parsed.v
  } catch { return null }
}

/** Whether a silent sign-in is worth attempting at boot. Lets the caller
 *  hold the login screen back rather than flashing it. */
export function hasPersistedSession(): boolean {
  if (!HAS_USER_OAUTH) return false
  return !!persistedRefreshToken()
}

function getRefreshToken(): string | null {
  const inTab = sessionStorage.getItem(STORAGE_KEY_REFRESH)
  if (inTab) return inTab
  return persistedRefreshToken()
}

function storeTokens(resp: TokenResponse) {
  sessionStorage.setItem(STORAGE_KEY_TOKEN, resp.access_token)
  if (resp.refresh_token) sessionStorage.setItem(STORAGE_KEY_REFRESH, resp.refresh_token)
  sessionStorage.setItem(STORAGE_KEY_EXPIRY, String(Date.now() + (resp.expires_in - 60) * 1000))
  // Mirror the refresh token for a remembered session, re-stamped on every
  // rotation so an active user's 30 days roll forward rather than expiring
  // mid-use.
  if (resp.refresh_token && rememberWanted()) {
    try {
      localStorage.setItem(STORAGE_KEY_REFRESH,
        JSON.stringify({ v: resp.refresh_token, t: Date.now() }))
    } catch { /* private mode */ }
  }
}

export function getCachedUsername(): string | null {
  return sessionStorage.getItem(STORAGE_KEY_USERNAME)
}
export function setCachedUsername(name: string) {
  sessionStorage.setItem(STORAGE_KEY_USERNAME, name)
}

export function clearOAuthState() {
  sessionStorage.removeItem(STORAGE_KEY_TOKEN)
  sessionStorage.removeItem(STORAGE_KEY_REFRESH)
  sessionStorage.removeItem(STORAGE_KEY_EXPIRY)
  sessionStorage.removeItem(STORAGE_KEY_USERNAME)
  sessionStorage.removeItem(STORAGE_KEY_STATE)
  sessionStorage.removeItem(STORAGE_KEY_VERIFIER)
  // Signing out ends a remembered session too, on this device.
  try {
    localStorage.removeItem(STORAGE_KEY_REFRESH)
    localStorage.removeItem(STORAGE_KEY_REMEMBER)
    localStorage.removeItem(STORAGE_KEY_STATE)
    localStorage.removeItem(STORAGE_KEY_VERIFIER)
  } catch { /* private mode */ }
}

/* ------------------------------------------------------------------ */
/*  PKCE primitives                                                   */
/* ------------------------------------------------------------------ */

/** URL-safe base64 of a byte array, with padding stripped. */
function base64UrlEncode(bytes: Uint8Array): string {
  let s = ""
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/** Make a 32-byte cryptographically random URL-safe string used as the
 *  PKCE code_verifier. RFC 7636 requires 43-128 chars; 32 random bytes
 *  base64url-encoded is 43 chars, which is the minimum and recommended. */
function makeCodeVerifier(): string {
  const arr = new Uint8Array(32)
  crypto.getRandomValues(arr)
  return base64UrlEncode(arr)
}

/** SHA-256 of the verifier, base64url-encoded - the code_challenge value
 *  sent at /authorize when method=S256. */
async function makeCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest("SHA-256", data)
  return base64UrlEncode(new Uint8Array(digest))
}

function makeStateNonce(): string {
  const arr = new Uint8Array(16)
  crypto.getRandomValues(arr)
  return base64UrlEncode(arr)
}

/* ------------------------------------------------------------------ */
/*  Authorization Code + PKCE flow                                    */
/* ------------------------------------------------------------------ */

export async function beginLogin(remember = false): Promise<void> {
  if (!HAS_USER_OAUTH) {
    throw new Error("OAuth client not configured. Set VITE_OAUTH_CLIENT_ID and VITE_OAUTH_REDIRECT_URI in .env.local.")
  }
  // Recorded before leaving, because the page reloads on the way back and
  // anything held in memory is gone by then.
  setRememberMe(remember)
  const state = makeStateNonce()
  const codeVerifier = makeCodeVerifier()
  const codeChallenge = await makeCodeChallenge(codeVerifier)

  // Persist the OAuth one-shot values across the redirect to meta. We
  // write to BOTH sessionStorage and localStorage:
  //   - sessionStorage is preferred (cleared at tab close, scoped to
  //     this tab - minimal CSRF / XSS surface)
  //   - localStorage is a fallback for "lite" browsers (Via, some
  //     in-app webviews) that wipe sessionStorage between the
  //     leaving and returning navigation, breaking PKCE handshake.
  //     The value is timestamped and read back with a 10-minute TTL,
  //     after which it is treated as stale and rejected.
  // We always clear both after a successful exchange.
  const stamped = JSON.stringify({ v: state, t: Date.now() })
  const stampedV = JSON.stringify({ v: codeVerifier, t: Date.now() })
  try { sessionStorage.setItem(STORAGE_KEY_STATE, state) } catch { /* private mode */ }
  try { sessionStorage.setItem(STORAGE_KEY_VERIFIER, codeVerifier) } catch { /* */ }
  try { localStorage.setItem(STORAGE_KEY_STATE, stamped) } catch { /* */ }
  try { localStorage.setItem(STORAGE_KEY_VERIFIER, stampedV) } catch { /* */ }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: OAUTH_CLIENT_ID,
    redirect_uri: OAUTH_REDIRECT_URI,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256"
  })
  window.location.href = OAUTH_AUTHORIZE_URL + "?" + params.toString()
}

export async function handleCallback(): Promise<boolean> {
  const url = new URL(window.location.href)
  const code = url.searchParams.get("code")
  const state = url.searchParams.get("state")
  const error = url.searchParams.get("error")
  const errorDescription = url.searchParams.get("error_description")

  if (error) {
    throw new Error("OAuth provider returned error: " + error +
      (errorDescription ? " - " + errorDescription : ""))
  }
  if (!code) return false

  // Immediately strip the OAuth params from the URL so that any re-mount of
  // this component (e.g. from HMR, browser back, or the SPA reloading assets)
  // sees a clean URL and exits at the `if (!code) return false` guard above.
  // This must happen synchronously - before any await - so it takes effect
  // before the async token exchange begins.
  try {
    const clean = new URL(window.location.href)
    clean.searchParams.delete("code")
    clean.searchParams.delete("state")
    clean.searchParams.delete("error")
    clean.searchParams.delete("error_description")
    window.history.replaceState(null, "", clean.toString())
  } catch { /* replaceState may be blocked in some sandboxed contexts */ }

  // Read state from sessionStorage first (preferred), fall back to the
  // localStorage copy that "lite" browsers like Via require because
  // they wipe sessionStorage across the OAuth redirect. The fallback
  // value is rejected if older than 10 minutes (login-flow window).
  const TEN_MIN = 10 * 60 * 1000
  function readBoth(sessKey: string, localKey: string): string | null {
    const fromSession = sessionStorage.getItem(sessKey)
    if (fromSession) return fromSession
    const fromLocal = localStorage.getItem(localKey)
    if (!fromLocal) return null
    try {
      const parsed = JSON.parse(fromLocal) as { v?: string; t?: number }
      if (!parsed.v || !parsed.t) return null
      if (Date.now() - parsed.t > TEN_MIN) return null
      return parsed.v
    } catch { return null }
  }
  function clearBoth() {
    try { sessionStorage.removeItem(STORAGE_KEY_STATE) } catch { /* */ }
    try { sessionStorage.removeItem(STORAGE_KEY_VERIFIER) } catch { /* */ }
    try { localStorage.removeItem(STORAGE_KEY_STATE) } catch { /* */ }
    try { localStorage.removeItem(STORAGE_KEY_VERIFIER) } catch { /* */ }
  }

  const expectedState = readBoth(STORAGE_KEY_STATE, STORAGE_KEY_STATE)
  if (!expectedState || expectedState !== state) {
    clearBoth()
    throw new Error("OAuth state mismatch - possible CSRF. Please try logging in again.")
  }
  const codeVerifier = readBoth(STORAGE_KEY_VERIFIER, STORAGE_KEY_VERIFIER)
  clearBoth()
  if (!codeVerifier) {
    throw new Error("OAuth flow lost its PKCE verifier (browser storage cleared mid-flow). Please try logging in again.")
  }

  // Build the token exchange body.
  // If this consumer was registered as "confidential client: Yes" on Wikimedia,
  // client_secret is required. If registered as public (confidential: No),
  // PKCE alone is sufficient and client_secret must be omitted.
  const tokenParams: Record<string, string> = {
    grant_type: "authorization_code",
    code,
    client_id: OAUTH_CLIENT_ID,
    redirect_uri: OAUTH_REDIRECT_URI,
    code_verifier: codeVerifier
  }
  if (OAUTH_CLIENT_SECRET) {
    tokenParams.client_secret = OAUTH_CLIENT_SECRET
  }

  const body = new URLSearchParams(tokenParams)
  const resp = await fetch(OAUTH_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString()
  })

  if (!resp.ok) {
    let msg = "Token exchange failed: HTTP " + resp.status
    try {
      const j = await resp.json()
      if (j.error) msg += " - " + j.error
      if (j.error_description) msg += ": " + j.error_description
    } catch { /* */ }
    // Hint for the most common misconfiguration
    if (!OAUTH_CLIENT_SECRET) {
      msg += "\n\nHint: If your OAuth consumer is registered as 'confidential client: Yes' on Wikimedia, set VITE_OAUTH_CLIENT_SECRET in your .env.local and rebuild."
    }
    throw new Error(msg)
  }

  const data: TokenResponse = await resp.json()
  if (!data.access_token) throw new Error("Token exchange returned no access_token")
  storeTokens(data)
  installBackButtonTrap()
  return true
}

/**
 * Install a single-press back-button trap. Without this, pressing back
 * from the upload page navigates to meta.wikimedia.org's OAuth authorize
 * page (which is in browser history because that's where we sent the
 * user to log in) - and the user sees the "Allow / Cancel" prompt again
 * even though they already authorized, which is annoying after an
 * accidental back-press.
 *
 * We can't actually remove the meta entry from history (browsers don't
 * permit cross-origin history mutation), but we can push a sentinel
 * entry on top of it. When the user presses back, popstate fires inside
 * our origin and we push the sentinel forward again - turning a
 * single back-press into a no-op. A *deliberate* second back-press
 * (or browser-supplied long-press menu) still works for users who
 * actually want to leave.
 */
function installBackButtonTrap() {
  if ((window as unknown as { __c2cBackTrap?: boolean }).__c2cBackTrap) return
  ;(window as unknown as { __c2cBackTrap?: boolean }).__c2cBackTrap = true
  // Push a sentinel state on top of the current history entry.
  history.pushState({ c2c: "auth-ok" }, "", window.location.href)
  window.addEventListener("popstate", (e) => {
    // If the user's back-press would land them on a non-c2c-controlled
    // entry (the meta authorize page), the popstate event fires with
    // the previous state still attached to *our* entry. Re-push the
    // sentinel to keep them on the upload screen.
    if (!e.state || (e.state as { c2c?: string }).c2c !== "auth-ok") {
      history.pushState({ c2c: "auth-ok" }, "", window.location.href)
    }
  })
}

export async function refreshAccessToken(): Promise<string | null> {
  if (!HAS_USER_OAUTH) return LEGACY_ACCESS_TOKEN || null
  const refresh = getRefreshToken()
  if (!refresh) return null

  // Per RFC 6749 §6, public clients exchange refresh tokens with client_id
  // only - no client_secret. Confidential clients must include it.
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refresh,
    client_id: OAUTH_CLIENT_ID,
    ...(OAUTH_CLIENT_SECRET ? { client_secret: OAUTH_CLIENT_SECRET } : {})
  })

  // Only a genuine refusal of the grant ends the session. Failing to get an
  // answer at all - the network dropped, the server is down or rate-limiting,
  // the body arrived unreadable - says nothing about whether the refresh token
  // is still good, and clearing the session for it signed people out over a
  // blip. Those cases keep the session and return null: the request that
  // needed a token fails as it would have anyway, and the next one retries.
  let resp: Response
  try {
    resp = await fetch(OAUTH_TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString()
    })
  } catch {
    return null
  }
  if (!resp.ok) {
    if (isTransientStatus(resp.status)) return null
    // 400 invalid_grant, 401 invalid_client and the like: the refresh token
    // is invalid, expired or revoked, and keeping it would only repeat that.
    clearOAuthState()
    return null
  }
  let data: TokenResponse | null
  try {
    data = await resp.json()
  } catch {
    return null
  }
  if (!data || !data.access_token) { clearOAuthState(); return null }
  try {
    storeTokens(data)
  } catch {
    // Storage refused the tokens (quota, private mode). As before.
    clearOAuthState()
    return null
  }
  return data.access_token
}

/** Statuses meaning "no answer right now", as opposed to "grant refused". */
function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500
}

/**
 * Sign in again with no user interaction, using a remembered refresh token.
 * Returns true when there is a usable access token afterwards.
 *
 * Never throws: a failure just means the login screen shows. A transient
 * network failure leaves the remembered session in place (see
 * refreshAccessToken), so a blip does not sign anyone out.
 */
export async function restoreSession(): Promise<boolean> {
  if (!HAS_USER_OAUTH) return !!LEGACY_ACCESS_TOKEN
  if (getAccessToken()) return true
  if (!persistedRefreshToken()) return false
  return !!(await refreshAccessToken())
}

export function logout(): void {
  clearOAuthState()
  window.location.href = "/"
}